import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import * as React from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { SuccessMessageControl } from "@tlc/ls2pac/app/controls/successMessageControl";
import { useLanguage } from "@tlc/ls2pac/components/Language";
import { ListService, ListType } from "../ListService";

export interface RenameListDialogProps {
  open: boolean;
  name: string;
  id: number;
  type: ListType;
  title: string;
  errorMessage: string;
  successMessage: string;
  onClose: () => void;
  onSave: (newName: string) => void;
}

export interface RenameListFormInput {
  listName: string;
}

export const RenameListDialog: React.FC<RenameListDialogProps> = ({
  name,
  id,
  type,
  open,
  title,
  errorMessage,
  successMessage,
  onClose,
  onSave,
}) => {
  const { translate } = useLanguage();
  const [renameError, setRenameError] = React.useState<boolean>(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<RenameListFormInput>({
    mode: "onBlur",
    defaultValues: {
      listName: name,
    },
  });

  const onSubmit: SubmitHandler<RenameListFormInput> = async ({ listName }) => {
    setRenameError(false);

    try {
      const success = await ListService.renameList(id, type, listName);

      if (success) {
        SuccessMessageControl.show(successMessage);
        onSave(listName);
        reset();
      } else {
        setRenameError(true);
      }
    } catch (e) {
      setRenameError(true);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        component: "form",
        noValidate: true,
        onSubmit: handleSubmit(onSubmit),
      }}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent sx={{ pb: 0 }}>
        {renameError && <Alert severity="error">{errorMessage}</Alert>}
        <TextField
          fullWidth
          style={{ marginTop: "16px" }}
          label={title}
          {...register("listName", { required: true })}
          error={!!errors.listName}
          helperText={errors.listName ? translate("requiredFieldError") : " "}
          required
          autoFocus
        ></TextField>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} type="button">
          {translate("cancel")}
        </Button>
        <Button type="submit">{translate("ok")}</Button>
      </DialogActions>
    </Dialog>
  );
};
